<template>
  <b-container>
    <b-modal
      v-model="menuEditVisible"
      title="edit menu"
      @ok="onModalOk"
      size="xl"
      modal-class="view-editor-modal"
    >
      <template #title>
        edit menu
        <modal-help topic="edit-menu" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>
          <b-tab title="Menu" active>
            <div class="tab-section">
              <div class="section-heading">Items</div>
              <p class="section-desc">
                Drag a page into the menu, or click +. The key button keeps the record open on this page, so the linked page can show and create records related to that same record. The square shows that link as a button, in the same style as the other buttons in the app. Add code button runs JavaScript when it is clicked, including functions from the custom code files in app settings. The shield shows or hides a link for a signed-in user.
              </p>
              <div class="menu-edit-panels">
                <div class="menu-edit-pages">
                  <draggable
                    :list="availablePages"
                    :group="{ name: 'menu', pull: 'clone', put: false }"
                    :clone="onCloneFromAvailable"
                    :sort="false"
                    :move="checkMenuMove"
                    item-key="uniqueTitle"
                  >
                    <template #item="{ element: page }">
                      <div
                        class="available-page-item"
                        :class="{ 'already-in-menu': page.alreadyInMenu }"
                      >
                        <font-awesome-icon icon="grip-vertical" class="page-drag-icon" />
                        <span class="page-label">{{ page.title }}</span>
                        <span v-if="page.parentUniqueTitle" class="page-parent">{{ page.parentUniqueTitle }}</span>
                        <span v-if="page.alreadyInMenu" class="in-menu-badge">in menu</span>
                        <button v-else type="button" class="add-page-btn" @click="addPageToMenu(page)">+</button>
                      </div>
                    </template>
                  </draggable>
                </div>
                <div class="menu-edit-items">
                  <draggable
                    v-model="menuItems"
                    :group="{ name: 'menu', pull: true, put: true }"
                    item-key="id"
                    handle=".menu-drag-handle"
                    filter=".dropdown-children"
                    :preventOnFilter="false"
                    ghost-class="drag-ghost"
                    :empty-insert-threshold="80"
                    :move="checkMenuMove"
                    data-menu-depth="1"
                    class="menu-items-draggable"
                  >
                    <template #item="{ element: item, index }">
                      <MenuItemNode
                        :item="item"
                        :depth="1"
                        @remove="removeMenuItem(index)"
                      />
                    </template>
                    <template #footer>
                      <div v-if="menuItems.length === 0" class="empty-menu-hint">
                        No menu items yet.
                      </div>
                    </template>
                  </draggable>
                  <div class="menu-edit-actions">
                    <button type="button" class="add-dropdown-btn" @click="addDropdownGroup">add menu</button>
                    <button type="button" class="add-dropdown-btn" @click="addValueItem">add value</button>
                    <button type="button" class="add-dropdown-btn" @click="addCodeButton">add code button</button>
                  </div>
                </div>
              </div>
            </div>
          </b-tab>
          <b-tab title="General">
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" placeholder="menu unique title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" placeholder="menu title" required />
                </label>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>
    <UserPermissionRuleEditor
      :visible="permissionEditorVisible"
      :rules="permissionEditorRules"
      :spreadsheets="spreadsheets"
      :pages="pages"
      subject="this link"
      :application-unique-title="application"
      @update:visible="permissionEditorVisible = $event"
      @save="saveLinkPermissionRules"
    />
  </b-container>
</template>

<script>
  import draggable from 'vuedraggable'
  import _ from 'lodash'
  import MenuItemNode from './page_layouts/MenuItemNode.vue'
  import normalizeMenuItems from './page_layouts/normalize-menu-items.js'
  import canPlaceMenuItem from './page_layouts/menu-nesting.js'
  import { pageIsUnder } from '../../../shared/page-menu.js'
  import UserPermissionRuleEditor from './lib/UserPermissionRuleEditor.vue'

  export default {
    name: 'menuEdit',
    components: { draggable, MenuItemNode, UserPermissionRuleEditor },
    provide () {

      return { menuEditor: this }
    },
    data () {

      return {
        menuEditVisible: false,
        activeTab: 0,
        uniqueTitle: '',
        title: '',
        menuItems: [],
        pages: [],
        spreadsheets: [],
        currentPageUniqueTitle: '',
        applicationUniqueTitle: '',
        parentDesigner: null,
        editingItemId: null,
        previewValues: {},
        previewRequestId: 0,
        supportsRecordLinks: true,
        supportsButtonLinks: true,
        supportsCodeButtons: true,
        permissionEditorVisible: false,
        permissionTarget: null
      }
    },
    created () {

      this.queuePreviewValues = _.debounce(() => {

        this.loadPreviewValues()
      }, 300)
    },
    watch: {
      menuItems: {
        deep: true,
        handler () {

          if (this.menuEditVisible && this.queuePreviewValues) this.queuePreviewValues()
        }
      }
    },
    computed: {
      application () {

        return this.applicationUniqueTitle || (this.$route && this.$route.params && this.$route.params.application) || ''
      },
      permissionEditorRules () {

        return (this.permissionTarget && this.permissionTarget.userPermissionRules) || { rules: [] }
      },
      appHasLogin () {

        const hasLogin = (components) => {

          if (!components) return false

          for (const comp of components) {

            if (comp.type === 'login' || comp.type === 'registration') return true
            if (comp.components && hasLogin(comp.components)) return true
          }

          return false
        }

        return (this.pages || []).some(page => hasLogin(page.components))
      },
      availablePages () {

        const usedPageTitles = new Set()

        const collectLinks = (items) => {

          for (const item of items || []) {

            if (item.type === 'link' && item.pageUniqueTitle) usedPageTitles.add(item.pageUniqueTitle)

            collectLinks(item.children)
          }
        }

        collectLinks(this.menuItems)

        return (this.pages || [])
          .filter(page => page && page.uniqueTitle)
          .map(page => ({
            ...page,
            alreadyInMenu: usedPageTitles.has(page.uniqueTitle)
          }))
      }
    },
    methods: {
      showEditMenuComponent () {

        this.activeTab = 0
        this.editingItemId = null
        this.menuItems = normalizeMenuItems(this.menuItems)
        this.menuEditVisible = true
        this.loadPreviewValues()
      },
      onModalOk (event) {

        if (!String(this.title).trim() || !String(this.uniqueTitle).trim()) {

          event.preventDefault()

          return
        }

        this.save()
      },
      save () {

        this.parentDesigner.saveActiveComponent({
          uniqueTitle: this.uniqueTitle,
          title: this.title,
          menuItems: _.cloneDeep(this.menuItems)
        })
      },
      generateItemId () {

        return `item_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
      },
      linkForPage (page) {

        return {
          id: this.generateItemId(),
          type: 'link',
          label: page.title,
          pageUniqueTitle: page.uniqueTitle,
          inheritAccessKey: pageIsUnder(this.pages, this.currentPageUniqueTitle, page.uniqueTitle)
        }
      },
      addPageToMenu (page) {

        this.menuItems.push(this.linkForPage(page))
      },
      onCloneFromAvailable (original) {

        return this.linkForPage(original)
      },
      newDropdownItem () {

        return {
          id: this.generateItemId(),
          type: 'dropdown',
          label: 'New Group',
          children: []
        }
      },
      addDropdownGroup () {

        this.menuItems.push(this.newDropdownItem())
      },
      newValueItem () {

        return {
          id: this.generateItemId(),
          type: 'value',
          label: 'Count',
          children: [],
          valueConfig: {
            kind: 'count',
            spreadSheet: '',
            field: '',
            formula: 'COUNT({id})',
            filters: [],
            filterMatchMode: 'all',
            scopeToLoggedInUser: false
          }
        }
      },
      addValueItem () {

        this.menuItems.push(this.newValueItem())
      },
      newCodeItem () {

        return {
          id: this.generateItemId(),
          type: 'code',
          label: 'Run',
          code: ''
        }
      },
      addCodeButton () {

        this.menuItems.push(this.newCodeItem())
      },
      removeMenuItem (index) {

        this.menuItems.splice(index, 1)
      },
      checkMenuMove (evt) {

        const dragged = evt.draggedContext && evt.draggedContext.element
        const depth = Number(evt.to && evt.to.dataset && evt.to.dataset.menuDepth)

        if (!depth) return true

        return canPlaceMenuItem(dragged, depth)
      },
      startEditing (itemId) {

        this.editingItemId = itemId
      },
      focusInput (e) {

        const el = e.el

        if (el && el.focus) {

          el.focus()
          el.select()
        }
      },
      toggleFlag (item, flag) {

        item[flag] = !item[flag]
      },
      openLinkPermissionRules (item) {

        this.permissionTarget = item
        this.permissionEditorVisible = true
      },
      saveLinkPermissionRules (rules) {

        if (!this.permissionTarget) return

        this.permissionTarget.userPermissionRules = rules
        this.permissionEditorVisible = false
      },
      toggleAlignRight (item) {

        if (item.alignRight) delete item.alignRight
        else item.alignRight = true
      },
      previewText (item) {

        const entry = this.previewValues[item.id]

        if (!entry || entry.display === '' || entry.display == null) return '—'

        return entry.display
      },
      async loadPreviewValues () {

        const requestId = ++this.previewRequestId
        const hasValueItem = (items) => (items || []).some(item => item.type === 'value' || hasValueItem(item.children))

        if (!hasValueItem(this.menuItems) || !this.application) {

          this.previewValues = {}

          return
        }

        try {

          const res = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/menu-values/preview`, {
            items: this.menuItems
          }, {
            headers: { 'application-unique-title': this.application }
          })

          if (requestId !== this.previewRequestId) return

          this.previewValues = (res.data && res.data.values) || {}
        } catch (error) {

          if (requestId !== this.previewRequestId) return

          this.previewValues = {}
        }
      }
    }
  }
</script>

<style scoped>
.menu-edit-panels {
  display: flex;
  gap: 12px;
  min-height: 220px;
}

.menu-edit-pages,
.menu-edit-items {
  flex: 1;
  min-width: 0;
}

.menu-edit-pages {
  max-height: 360px;
  overflow: auto;
}

.available-page-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 4px;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  background: #161614;
  color: #e0e0de;
  cursor: grab;
}

.available-page-item.already-in-menu {
  opacity: 0.45;
}

.page-drag-icon {
  color: #a8a8a4;
  font-size: 0.7rem;
}

.page-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-parent,
.in-menu-badge {
  font-size: 0.7rem;
  color: #8a8a86;
  font-family: monospace;
}

.in-menu-badge {
  color: #9ad12b;
}

.add-page-btn,
.add-dropdown-btn {
  border: 1px solid #3a3a37;
  background: transparent;
  color: #c8c8c4;
  border-radius: 3px;
  cursor: pointer;
}

.add-page-btn {
  width: 22px;
  height: 22px;
}

.menu-edit-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.add-dropdown-btn {
  padding: 4px 8px;
  font-size: 0.8rem;
}

.empty-menu-hint {
  color: #8a8a86;
  font-size: 0.85rem;
  padding: 8px 0;
}

.menu-items-draggable {
  min-height: 48px;
}
</style>
